import React, {
	useEffect,
	forwardRef,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import styles from './CSSZSickLeaveCertificateList.module.scss';
import {
	CSSZSickLeaveCertificate,
	CSSZUser,
} from '../../Interfaces/CSSZ.interface';
import { transformDocumentToSickLeaveCertificate } from '../../Helpers/transformDocumentToSickLeaveCertificate.helper';
import CSSZSickLeaveCertificateDetail from '../CSSZSickLeaveCertificateDetail/CSSZSickLeaveCertificateDetail';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import { useFlatDataList } from '../../../Base/Hooks/useFlatDataList';
import {
	DocumentType,
	useCsszSickLeavesQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import FlatList from '../../../Base/Components/FlatList';
import {
	buildSickLeaveYearRows,
	SickLeaveListRow,
} from '../../Helpers/buildSickLeaveYearRows.helper';
import CSSZSickLeaveCertificateListRow from './CSSZSickLeaveCertificateListRow';

export type CSSZSickLeaveCertificateListHandle = {
	refresh: () => void;
};

interface Props {
	user: CSSZUser;
	initialActiveItemId?: string;
}

const wrapperId = 'CSSZSickLeaveCertificateListWrapper';

const CSSZSickLeaveCertificateList = forwardRef<
	CSSZSickLeaveCertificateListHandle,
	Props
>(({ user, initialActiveItemId }, ref) => {
	const { flatData, pagination, getNextPage, appendData, reset } =
		useFlatDataList<CSSZSickLeaveCertificate>();

	const { data, isLoading, refetch, isFetching } = useCsszSickLeavesQuery({
		where: {
			userId: user.id,
			types: [DocumentType.SickLeave],
		},
		pagination,
	});

	const [activeItem, setActiveItem] = useState<CSSZSickLeaveCertificate | null>(
		null,
	);
	const didAutoselectRef = useRef(false);

	const currentYear = useMemo(() => new Date().getFullYear(), []);
	const [openYears, setOpenYears] = useState<Record<number, boolean>>({
		[currentYear]: true,
	});

	useEffect(() => {
		const transformedData: CSSZSickLeaveCertificate[] =
			data?.documents?.data
				?.map(item => transformDocumentToSickLeaveCertificate(item))
				.filter(
					(item): item is CSSZSickLeaveCertificate => item !== undefined,
				) ?? [];
		appendData(transformedData);
	}, [data]);

	useEffect(() => {
		setActiveItem(prev => {
			if (!prev) return prev;
			const updated = flatData.find(item => item.id === prev.id);
			return updated ?? prev;
		});
	}, [flatData]);

	useEffect(() => {
		if (!initialActiveItemId || didAutoselectRef.current) {
			return;
		}

		// User picked another row (or selection no longer matches URL) — do not override when initial loads later
		if (activeItem != null && activeItem.id !== initialActiveItemId) {
			didAutoselectRef.current = true;
			return;
		}

		const found = flatData.find(i => i.id === initialActiveItemId);

		if (found) {
			setActiveItem(found);
			didAutoselectRef.current = true;
		}
	}, [flatData, initialActiveItemId, activeItem]);

	const documentCount = data?.documents?.data?.length ?? 0;
	useEffect(() => {
		setActiveItem(prev => {
			if (prev == null) {
				return null;
			}
			if (flatData.length === 0) {
				if (documentCount === 0 && !isLoading && !isFetching) {
					return null;
				}
				return prev;
			}
			return flatData.find(i => i.id === prev.id) ?? null;
		});
	}, [flatData, documentCount, isLoading, isFetching]);

	const refresh = () => {
		reset();
		refetch();
	};

	useImperativeHandle(ref, () => ({ refresh }), [refresh]);

	const handleSelectItem = async (item: CSSZSickLeaveCertificate) => {
		didAutoselectRef.current = true;
		setActiveItem(item);
	};

	const toggleYear = (year: number) => {
		setOpenYears(prev => ({
			...prev,
			[year]: !(prev[year] ?? year === currentYear),
		}));
	};

	const rows: SickLeaveListRow[] = useMemo(
		() =>
			buildSickLeaveYearRows({
				flatData,
				openYears,
				currentYear,
			}),
		[currentYear, flatData, openYears],
	);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.list}>
				<ScrollContainer
					wrapperId={wrapperId}
					className={styles.scrollContainer}>
					<FlatList
						onRefresh={refetch}
						onLoadMore={getNextPage}
						data={rows}
						isLoading={isLoading || isFetching}
						hasMoreData={data?.documents?.meta?.hasNextPage || false}
						scrollableTargetId={wrapperId}
						itemKey='id'
						className={styles.flatList}
						renderItem={(row: SickLeaveListRow) => (
							<CSSZSickLeaveCertificateListRow
								row={row}
								activeItemId={activeItem?.id ?? null}
								onToggleYear={toggleYear}
								onSelectCertificate={handleSelectItem}
							/>
						)}
					/>
				</ScrollContainer>
			</div>
			<div className={styles.detail}>
				{activeItem && (
					<CSSZSickLeaveCertificateDetail
						item={activeItem}
						key={activeItem.id}
						user={user}
						onJobSuccess={refresh}
					/>
				)}
			</div>
		</div>
	);
});

export default CSSZSickLeaveCertificateList;
